import {
  ReportStatus,
  discardReportState,
  discardReportStateDefault,
  hasReportContentChangedState,
  reportStatusState,
} from '../domains/reporter/Reporter/state';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCurrentCaseReport } from './useCurrentCaseReport';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useToasterDispatch } from '../common/ui/Toaster/Toaster';
import {
  CHANGE_WORKLIST_ITEM_STATUS,
  RESET_AND_RELEASE_WORKLIST_ITEM,
} from '../modules/Apollo/queries';
import { useMutation } from '@apollo/client';
import { useCurrentCaseId } from './useCurrentCase';
import usePrevious from 'react-use/esm/usePrevious';
import { logger } from 'modules/logger';
import {
  worklistUpdateBc,
  WorklistRefetchReason,
} from 'modules/EventsManager/worklistUpdateChannel';
import { PageTypes } from '../utils/pageTypes';
import type { ReportStatuses } from '../domains/reporter/Reporter/state';
import { focusEvent, dispatchExtensionEvent } from 'domains/extension/extensionEventCreators';
import { useWorklistAutoLoad, useMaybeAutoloadNextCase } from './useWorklistAutoLoad';
import {
  useResetReviewedStudiesByDate,
  useReviewedStudiesByDate,
} from '../domains/viewer/ViewportDre/modules/imaging/useTrackViewedSlices';
import { getWorklistItemDraftState } from 'domains/worklist/Worklist/utils/getWorklistItemDraftState';
import { ToastKey } from 'common/ui/Toaster/Toast';
import { useBroadcastRefreshCurrentCase } from 'common/CurrentCase/useBroadcastRefreshCurrentCase';
import { ReportExitAction } from 'domains/reporter/RichTextEditor/analytics/performance';
import type { ReviewedStudyByDate } from '../domains/viewer/ViewportDre/modules/imaging/useTrackViewedSlices';
import { CASE_REVIEWED_STUDIES_CACHE_KEY_PREFIX } from './useDeepLinkStudy';
import { WithApolloContext } from 'modules/Apollo/types';

type DiscardReportOptions = {
  onSuccess?: () => void;
  onError?: () => void;
  keepDraft?: boolean;
};

type UseDiscardReportReturnType = {
  handleDiscardReport: (options?: DiscardReportOptions) => Promise<void>;
  handleKeepDraft: (options?: Omit<DiscardReportOptions, 'keepDraft'>) => Promise<void>;
  handleOpenDiscardReportModal: () => void;
  handleCloseDiscardReportModal: () => void;
};

export const useDiscardReport = ({ context }: WithApolloContext): UseDiscardReportReturnType => {
  const { currentCaseReport: worklistItem, refetch: refetchCurrentCase } = useCurrentCaseReport();
  const [discardReportOptions, setDiscardReportOptions] = useRecoilState(discardReportState);
  const toastKey = useRef<Nullable<ToastKey>>('');
  const { closeToast } = useToasterDispatch();
  const [resetAndReleaseWorklistItem] = useMutation(RESET_AND_RELEASE_WORKLIST_ITEM, { context });
  const [changeWorklistItemStatus] = useMutation(CHANGE_WORKLIST_ITEM_STATUS, { context });
  const broadcastRefreshCase = useBroadcastRefreshCurrentCase();

  const currentCaseId = useCurrentCaseId();
  const prevCaseId = usePrevious(currentCaseId);
  const reportStatus = useRecoilValue<ReportStatuses>(reportStatusState);
  const hasReportContentChanged = useRecoilValue(hasReportContentChangedState);
  const resetReviewedStudiesByDate = useResetReviewedStudiesByDate();
  const reviewedStudiesByDate = useReviewedStudiesByDate({ context });
  const reviewedStudiesByDateRef = useRef<Map<string, ReviewedStudyByDate>>(new Map());
  const {
    isWorklistAutoLoadEnabled,
    loadDiscardedSmidsFromSessionStorage,
    saveDiscardedSmidsInSessionStorage,
  } = useWorklistAutoLoad({ context });

  const previousWorklistItemClaimedBy = usePrevious(worklistItem?.claimedBy?.id);
  useEffect(() => {
    reviewedStudiesByDateRef.current = new Map(reviewedStudiesByDate);
  }, [reviewedStudiesByDate]);

  const maybeAutoloadNextCase = useMaybeAutoloadNextCase({
    previousToastKey: toastKey.current,
    onToastDisplayed: (key) => {
      toastKey.current = key;
    },
  });

  const persistReviewedStudiesBeforeReset = useCallback(() => {
    // Persist the current comparison studies so reclaiming the case can restore them before the
    // viewer repopulates Recoil (which gets cleared by resetReviewedStudiesByDate).
    if (currentCaseId == null) return;

    try {
      const serialized = JSON.stringify(Array.from(reviewedStudiesByDateRef.current.entries()));
      localStorage.setItem(
        `${CASE_REVIEWED_STUDIES_CACHE_KEY_PREFIX}${currentCaseId}`,
        serialized ?? '[]'
      );
    } catch (error: any) {
      logger.warn('[useDiscardReport] Failed to cache reviewed studies prior to reset', error);
    }
  }, [currentCaseId]);

  const resetDiscardReportOptions = useCallback(() => {
    setDiscardReportOptions(discardReportStateDefault);
    if (toastKey.current != null) {
      closeToast(toastKey.current);
    }
  }, [setDiscardReportOptions, closeToast]);

  const isActionInFlight = useMemo(() => {
    return (
      (
        [
          ReportStatus.Submitting,
          ReportStatus.ProvisionalSubmit,
          ReportStatus.ProvisionalAddendumSubmit,
          ReportStatus.AddendumSubmitting,
        ] as ReportStatuses[]
      ).includes(reportStatus) || discardReportOptions.discarding === true
    );
  }, [discardReportOptions.discarding, reportStatus]);

  useEffect(() => {
    // if the user has selected a new case to look at after discarding previous case,
    // reset discardReportOptions
    // or if the worklist item was previously unclaimed and is now claimed and the
    // discard report options match, reset the discard report options
    if (
      worklistItem != null &&
      discardReportOptions.worklistItemSmid != null &&
      (worklistItem.smid !== discardReportOptions.worklistItemSmid ||
        (previousWorklistItemClaimedBy == null &&
          worklistItem?.claimedBy?.id != null &&
          worklistItem?.smid === discardReportOptions.worklistItemSmid))
    ) {
      resetDiscardReportOptions();
    }
  }, [
    discardReportOptions.worklistItemSmid,
    resetDiscardReportOptions,
    worklistItem,
    previousWorklistItemClaimedBy,
  ]);

  const handleDiscardReport = useCallback(
    async ({ onSuccess, onError, keepDraft = false }: DiscardReportOptions = {}) => {
      if (worklistItem == null || isActionInFlight) {
        return;
      }

      logger.info(`[useDiscardReport] Discard report flow has been (started) by user.`, {
        reportSmid: worklistItem.smid,
        keepDraft,
      });

      setDiscardReportOptions((prev) => ({ ...prev, discarding: true, worklistItemSmid: null }));

      const patientDescriptor = `${worklistItem.patientName ?? 'Unknown Name'}, ${
        worklistItem.studyDescription ?? 'Unknown Study Description'
      } (ACC: ${worklistItem.accessionNumber ?? 'Unknown'})`;

      const { draftType } = getWorklistItemDraftState(worklistItem);
      const hasDraftReport = draftType !== null;
      const shouldPreserveDraft = keepDraft && hasDraftReport;
      const defaultToastMsg = shouldPreserveDraft
        ? `Draft for ${patientDescriptor} has been preserved.`
        : `Report for ${patientDescriptor} has been discarded.`;
      if (hasDraftReport && !keepDraft) {
        logger.info(`[useDiscardReport] Calling reset and releasing on the worklist item`, {
          reportSmid: worklistItem.smid,
        });
        await resetAndReleaseWorklistItem({
          variables: {
            smid: worklistItem.smid,
          },
          onCompleted: async () => {
            await broadcastRefreshCase();

            // WF-1809: Save before triggering autoload so a concurrent autoload
            // (e.g. broadcast received by another window) reads the updated skip
            // set and doesn't re-serve the just-discarded case.
            const discardedSmids = loadDiscardedSmidsFromSessionStorage();
            discardedSmids.add(worklistItem.smid);
            saveDiscardedSmidsInSessionStorage(discardedSmids);

            await maybeAutoloadNextCase(isWorklistAutoLoadEnabled, defaultToastMsg, {
              reportExitAction: ReportExitAction.Discard,
              defaultToastMsg,
              isAddendum: false,
            });

            setDiscardReportOptions({
              discarding: false,
              open: false,
              worklistItemSmid: worklistItem.smid,
              error: null,
            });

            // Refetch to ensure the worklist item is updated so claimed by is null
            refetchCurrentCase != null && refetchCurrentCase();

            worklistUpdateBc.postMessage({
              smid: worklistItem.smid,
              reason: WorklistRefetchReason.Discard,
            });

            // edge case: if the user discards a case and then immediately claims it back, the reviewed studies will be reset
            persistReviewedStudiesBeforeReset();
            // ensures any deeplinks are reset in user's environment
            resetReviewedStudiesByDate();

            onSuccess && onSuccess();
          },
          onError: (e) => {
            const errorMessage = e.message.includes('400')
              ? 'Report could not be discarded due to server error.'
              : e.message;
            setDiscardReportOptions((prev) => ({
              ...prev,
              discarding: false,
              error: errorMessage,
            }));

            logger.error(
              `[useDiscardReport] Error occurred when discarding report: ${e.message}`,
              {
                reportSmid: worklistItem.smid,
              },
              e
            );

            onError && onError();
          },
        });
      } else {
        logger.info(
          keepDraft
            ? `[useDiscardReport] Keep draft - unclaiming without reset to preserve draft`
            : `[useDiscardReport] Report already sent - unclaiming without reset`,
          {
            reportSmid: worklistItem.smid,
            draftType,
            hasDraftReport,
            keepDraft,
          }
        );
        await changeWorklistItemStatus({
          variables: {
            smid: worklistItem.smid,
            claim: false,
            ...(keepDraft ? { resetReport: false } : {}),
          },
          onCompleted: async () => {
            await broadcastRefreshCase();

            const discardedSmids = loadDiscardedSmidsFromSessionStorage();
            discardedSmids.add(worklistItem.smid);
            saveDiscardedSmidsInSessionStorage(discardedSmids);

            await maybeAutoloadNextCase(isWorklistAutoLoadEnabled, defaultToastMsg, {
              reportExitAction: ReportExitAction.Discard,
              defaultToastMsg,
              isAddendum: false,
            });

            setDiscardReportOptions({
              discarding: false,
              open: false,
              worklistItemSmid: worklistItem.smid,
              error: null,
            });

            // Refetch to ensure the worklist item is updated so claimed by is null
            refetchCurrentCase != null && refetchCurrentCase();

            worklistUpdateBc.postMessage({
              smid: worklistItem.smid,
              reason: WorklistRefetchReason.Discard,
            });

            // edge case: if the user discards a case and then immediately claims it back, the reviewed studies will be reset
            persistReviewedStudiesBeforeReset();
            // ensures any deeplinks are reset in user's environment
            resetReviewedStudiesByDate();

            onSuccess && onSuccess();
          },
          onError: (e) => {
            const errorMessage = e.message.includes('400')
              ? 'Report could not be discarded due to server error.'
              : e.message;
            setDiscardReportOptions((prev) => ({
              ...prev,
              discarding: false,
              error: errorMessage,
            }));

            logger.error(
              `[useDiscardReport] Error occurred when discarding report: ${e.message}`,
              {
                reportSmid: worklistItem.smid,
              },
              e
            );

            onError && onError();
          },
        });
      }

      logger.info(`[useDiscardReport] Discard report flow has been (completed)`, {
        reportSmid: worklistItem.smid,
      });
    },
    [
      worklistItem,
      isActionInFlight,
      setDiscardReportOptions,
      resetAndReleaseWorklistItem,
      broadcastRefreshCase,
      maybeAutoloadNextCase,
      isWorklistAutoLoadEnabled,
      loadDiscardedSmidsFromSessionStorage,
      saveDiscardedSmidsInSessionStorage,
      refetchCurrentCase,
      persistReviewedStudiesBeforeReset,
      resetReviewedStudiesByDate,
      changeWorklistItemStatus,
    ]
  );

  const handleKeepDraft = useCallback(
    (options: Omit<DiscardReportOptions, 'keepDraft'> = {}) =>
      handleDiscardReport({ ...options, keepDraft: true }),
    [handleDiscardReport]
  );

  const handleOpenDiscardReportModal = useCallback(() => {
    if (worklistItem == null) {
      return;
    }

    const { draftType } = getWorklistItemDraftState(worklistItem);
    // Skip confirmation when there is nothing user-authored or drafted to lose.
    if (!hasReportContentChanged && draftType == null) {
      void handleDiscardReport();
      return;
    }

    setDiscardReportOptions({
      discarding: false,
      open: true,
      worklistItemSmid: null,
      error: null,
    });

    if (window.location.pathname.includes(PageTypes.REPORTER)) {
      dispatchExtensionEvent(focusEvent());
    }
  }, [handleDiscardReport, hasReportContentChanged, setDiscardReportOptions, worklistItem]);

  const handleCloseDiscardReportModal = useCallback(() => {
    setDiscardReportOptions({
      discarding: false,
      open: false,
      worklistItemSmid: null,
      error: null,
    });
  }, [setDiscardReportOptions]);

  useEffect(() => {
    if (currentCaseId !== prevCaseId && prevCaseId != null) {
      handleCloseDiscardReportModal();
    }
  }, [currentCaseId, discardReportOptions.open, handleCloseDiscardReportModal, prevCaseId]);

  return useMemo(
    () => ({
      handleDiscardReport,
      handleKeepDraft,
      handleOpenDiscardReportModal,
      handleCloseDiscardReportModal,
    }),
    [
      handleCloseDiscardReportModal,
      handleDiscardReport,
      handleKeepDraft,
      handleOpenDiscardReportModal,
    ]
  );
};
